import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  Clock,
  Users,
  BarChart3,
  TrendingUp,
  LogOut,
  Menu,
  X,
  UserCheck,
  Wallet,
  Palmtree,
  User,
  Target,
  Bell,
  Settings,
  DoorOpen,
  ListTodo,
  ChevronDown,
  ChevronRight,
  Bot
} from 'lucide-react';
import { Button } from './ui/button';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [projectsExpanded, setProjectsExpanded] = useState(
    location.pathname.startsWith('/projects') || location.pathname === '/tasks'
  );

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { 
      name: 'Proiecte', 
      href: '/projects', 
      icon: FolderKanban,
      submenu: [
        { name: 'Lista Proiecte', href: '/projects', icon: FolderKanban },
        { name: 'Task-uri', href: '/tasks', icon: ListTodo }
      ]
    },
    { name: 'Time Tracking', href: '/time-tracking', icon: Clock },
    ...(user?.role !== 'team_member' ? [{ name: 'Team', href: '/team', icon: Users }] : []),
    { name: 'Reports', href: '/reports', icon: BarChart3 },
    ...(user?.role !== 'team_member' ? [{ name: 'Client Profitability', href: '/client-profitability', icon: TrendingUp }] : []),
    { name: 'Performance', href: '/employee-performance', icon: UserCheck },
    { name: 'Concedii', href: '/leave', icon: Palmtree },
    { name: 'Învoiri', href: '/invoiri', icon: DoorOpen },
    ...(user?.role !== 'team_member' ? [{ name: 'Capacity Planning', href: '/capacity-planning', icon: Target }] : []),
    ...(user?.role === 'admin' ? [{ name: 'Salarii', href: '/salary-admin', icon: Wallet }] : []),
    ...(user?.role === 'admin' ? [{ name: 'Configurare Alerte', href: '/alerts-config', icon: Bell }] : []),
    ...(user?.role === 'admin' ? [{ name: 'Semmy', href: '/ai-admin', icon: Bot }] : []),
    ...(user?.role === 'admin' ? [{ name: 'Setări', href: '/settings', icon: Settings }] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-background">
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 transform border-r border-border bg-background transition-transform duration-200 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b border-border px-6">
            <div className="flex items-center gap-2">
              <img src="/adsem-logo.png" alt="Adsem" className="h-7 object-contain" />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              const hasSubmenu = item.submenu && item.submenu.length > 0;
              const isSubmenuActive = hasSubmenu && item.submenu.some(sub => location.pathname === sub.href);
              
              if (hasSubmenu) {
                return (
                  <div key={item.name}>
                    <button
                      onClick={() => setProjectsExpanded(!projectsExpanded)}
                      data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                      className={`w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm font-medium transition-smooth ${
                        isSubmenuActive
                          ? 'bg-brand-orange/10 text-brand-orange'
                          : 'text-foreground hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5" />
                        {item.name}
                      </div>
                      {projectsExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>
                    
                    {projectsExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = location.pathname === subItem.href;
                          return (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              data-testid={`nav-${subItem.name.toLowerCase().replace(' ', '-')}`}
                              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-smooth ${
                                isSubActive
                                  ? 'bg-brand-orange text-white'
                                  : 'text-foreground hover:bg-muted'
                              }`}
                              onClick={() => setSidebarOpen(false)}
                            >
                              <SubIcon className="h-4 w-4" />
                              {subItem.name}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-smooth ${
                    isActive
                      ? 'bg-brand-orange text-white'
                      : 'text-foreground hover:bg-muted'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className="border-t border-border p-4">
            <div className="mb-3 rounded-md bg-muted p-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Logged in as</p>
              <p className="mt-1 text-sm font-medium" data-testid="user-name">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              <div className="mt-2">
                <span className="inline-flex items-center rounded-md bg-secondary px-2 py-1 text-xs font-medium uppercase tracking-wide">
                  {user?.role?.replace('_', ' ')}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Link to="/profile" onClick={() => setSidebarOpen(false)}>
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  data-testid="profile-btn"
                >
                  <User className="mr-2 h-4 w-4" />
                  Profilul Meu
                </Button>
              </Link>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full justify-start"
                data-testid="logout-btn"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex h-16 items-center justify-between border-b border-border bg-background px-6 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="open-sidebar-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="font-outfit font-semibold">Adsem Performance</span>
          <div className="w-10" />
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-6">{children}</div>
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};